// A30 — cap-hit pause screen. Shown when entitlements report
// `workspace_paused = true` OR `daily_compute_minutes_used >=
// daily_compute_minutes_cap` AND tier == "free". Replaces the chat
// composer; receipts/history stay browseable. Subscribe button kicks
// off the Stripe Checkout flow.
//
// See Mission/pricing_and_tiers.md and ADR 0002.

import { useEffect, useState } from "react";
import {
  ApiError,
  createBillingCheckout,
  getEntitlements,
  getSavings,
  type Entitlements,
  type SavingsResponse,
} from "../api/client";

interface Props {
  workspaceId: number;
}

export function WorkspaceUpgradeGate({ workspaceId }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [savings, setSavings] = useState<SavingsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      getEntitlements(workspaceId),
      getSavings(workspaceId),
    ]).then(([entRes, savRes]) => {
      if (cancelled) return;
      if (entRes.status === "fulfilled") setEntitlements(entRes.value);
      if (savRes.status === "fulfilled") setSavings(savRes.value);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const subscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      const { checkout_url } = await createBillingCheckout();
      window.location.assign(checkout_url);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  const minutesUsed = entitlements?.daily_compute_minutes_used ?? 0;
  const minutesCap = entitlements?.daily_compute_minutes_cap ?? 240;
  const monthlySavingsDollars =
    (savings?.this_month.savings_micros ?? 0) / 1_000_000;

  return (
    <div
      className="flex h-full w-full items-center justify-center px-6 py-12"
      data-testid="workspace-upgrade-gate"
    >
      <div className="w-full max-w-lg rounded-md border border-edge bg-surface-raised p-6">
        <h2 className="text-xl font-semibold text-primary">
          You've used your daily compute
        </h2>
        <p className="mt-2 text-sm text-muted">
          Free tier covers {Math.round(minutesCap / 60)} hours of sandbox
          compute per day. You've used {Math.round(minutesUsed)} minutes today.
          Resume tomorrow when the cap resets, or unlock more with Premium.
        </p>
        {monthlySavingsDollars > 0 && (
          <p className="mt-3 text-xs text-accent">
            Heads up — you've saved ${monthlySavingsDollars.toFixed(2)} this
            month with Waytide compared to direct provider pricing.
          </p>
        )}
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void subscribe()}
            disabled={busy}
            className="rounded-md border border-accent bg-accent/15 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/25 disabled:opacity-50"
            data-testid="workspace-upgrade-gate-subscribe"
          >
            {busy ? "Opening checkout…" : "Subscribe to Premium"}
          </button>
          <p className="text-[0.625rem] text-faint">
            You can still browse receipts, history, and Settings while the
            workspace is paused. Sandbox state is preserved — agent dispatch
            resumes immediately on subscribe or at midnight UTC.
          </p>
        </div>
        {error && (
          <p
            className="mt-3 text-xs text-danger"
            data-testid="workspace-upgrade-gate-error"
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
